import { useCallback, useEffect, useMemo, useState } from "react"
import type { AppShell } from "../App"
import {
  decideSuggestion,
  loadAchievements,
  loadSuggestions,
  type Achievement,
  type AchievementsResponse,
  type WinSuggestion,
} from "../lib/api"
import { Button } from "../components/ui/button"
import { cn } from "../lib/utils"

/**
 * O período por omissão é a **semana passada**, não o mês: o ritual é olhar
 * para trás na segunda de manhã antes de fixar o foco da semana.
 */
type PeriodKey = "last_week" | "month" | "quarter"

function isoDay(date: Date): string {
  return date.toISOString().slice(0, 10)
}

function periodRange(key: PeriodKey): { from: string; to: string; label: string; sub: string } {
  const now = new Date()
  if (key === "last_week") {
    const monday = new Date(now)
    const offset = (now.getDay() + 6) % 7
    monday.setDate(now.getDate() - offset - 7)
    const nextMonday = new Date(monday)
    nextMonday.setDate(monday.getDate() + 7)
    return {
      from: isoDay(monday),
      to: isoDay(nextMonday),
      label: "Semana passada",
      sub: `${isoDay(monday)} → ${isoDay(new Date(nextMonday.getTime() - 86_400_000))}`,
    }
  }
  if (key === "month") {
    const first = new Date(now.getFullYear(), now.getMonth(), 1)
    const next = new Date(now.getFullYear(), now.getMonth() + 1, 1)
    return { from: isoDay(first), to: isoDay(next), label: "Mês", sub: isoDay(first).slice(0, 7) }
  }
  const quarterStartMonth = Math.floor(now.getMonth() / 3) * 3
  const first = new Date(now.getFullYear(), quarterStartMonth, 1)
  const next = new Date(now.getFullYear(), quarterStartMonth + 3, 1)
  return {
    from: isoDay(first),
    to: isoDay(next),
    label: "Trimestre",
    sub: `${now.getFullYear()}-Q${Math.floor(now.getMonth() / 3) + 1}`,
  }
}

const LEVEL_LABEL: Record<Achievement["level"], string> = {
  game_changer: "Game changer",
  big_win: "Big win",
  win: "Win",
}

const ORIGIN_LABEL: Record<string, string> = {
  manual: "Manual",
  chat: "Chat",
  crm: "CRM · automático",
  ai_draft: "IA · JIRA",
}

function hours(minutes: number): string {
  return `${Math.round(minutes / 60)} h`
}

export function SolvedPage({ shell }: { shell: AppShell }) {
  const [period, setPeriod] = useState<PeriodKey>("last_week")
  const [data, setData] = useState<AchievementsResponse | null>(null)
  const [suggestions, setSuggestions] = useState<WinSuggestion[]>([])
  const [error, setError] = useState<string | null>(null)

  const range = useMemo(() => periodRange(period), [period])

  const refresh = useCallback(() => {
    loadAchievements(shell.client, range.from, range.to)
      .then((res) => {
        setData(res)
        setError(null)
      })
      .catch((err) => setError((err as Error).message))
    loadSuggestions(shell.client).then(
      (res) => setSuggestions(res.suggestions),
      () => setSuggestions([]),
    )
  }, [shell.client, range.from, range.to])

  useEffect(refresh, [refresh])

  const decide = async (suggestion: WinSuggestion, decision: "registered" | "ignored") => {
    try {
      await decideSuggestion(
        shell.client,
        suggestion.key,
        decision,
        decision === "registered"
          ? {
              title: suggestion.title,
              result_types: suggestion.suggested_result_types,
              entity_id: suggestion.client_entity_id,
              project_reference: suggestion.project_reference,
              contract_id: suggestion.contract_id,
              milestone_id: suggestion.milestone_id,
              revenue: suggestion.amount,
              occurred_on: suggestion.occurred_at.slice(0, 10),
              source_kind: "crm",
            }
          : undefined,
      )
      refresh()
    } catch (err) {
      shell.bridge.toast({
        variant: "error",
        title: "Não foi possível decidir",
        description: (err as Error).message,
      })
    }
  }

  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-lg font-semibold text-destructive">Não foi possível ler o board</h1>
        <p className="mt-1 text-sm text-muted-foreground">{error}</p>
      </div>
    )
  }
  if (!data) return <div className="p-6 text-sm text-muted-foreground">A carregar resultados…</div>

  const { totals } = data
  const people = new Map<string, { wins: number; clients: Set<number>; top: string }>()
  for (const achievement of data.achievements) {
    // Linhas gravadas antes da resolução de nomes têm um UUID em owner_name;
    // mostrá-lo como se fosse uma pessoa é pior do que não mostrar ninguém.
    const isUuid = (v: string) =>
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v)
    const names = [achievement.owner_name, ...achievement.contributors]
      .filter(Boolean)
      .map((n) => String(n))
      .filter((n) => !isUuid(n)) as string[]
    for (const name of names) {
      const entry = people.get(name) ?? { wins: 0, clients: new Set<number>(), top: "win" }
      entry.wins++
      if (achievement.entity_id != null) entry.clients.add(achievement.entity_id)
      if (achievement.level === "game_changer") entry.top = "game_changer"
      else if (achievement.level === "big_win" && entry.top !== "game_changer") entry.top = "big_win"
      people.set(name, entry)
    }
  }

  return (
    <div className="mx-auto max-w-[1200px] px-6 pb-16">
      <div className="mt-6 flex flex-wrap items-center overflow-hidden rounded-md border">
        {(["last_week", "month", "quarter"] as PeriodKey[]).map((key) => {
          const info = periodRange(key)
          return (
            <button
              key={key}
              onClick={() => setPeriod(key)}
              aria-pressed={period === key}
              className={cn(
                "flex items-baseline gap-2 border-r px-4 py-2.5 text-sm font-medium last:border-r-0",
                period === key
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground",
              )}
            >
              {info.label}
              <span className="font-mono text-[10px] uppercase opacity-70">{info.sub}</span>
            </button>
          )
        })}
        <span className="ml-auto px-4 text-xs text-muted-foreground">
          Segunda de manhã: olha-se para trás antes de fixar o foco.
        </span>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-px overflow-hidden rounded-md border bg-border sm:grid-cols-5">
        {[
          { value: String(totals.count), label: "achievements" },
          { value: String(totals.clients), label: "clientes impactados" },
          {
            value: hours(totals.time_saved_estimated_minutes + totals.time_saved_measured_minutes),
            label: "poupadas por semana",
            sub: `${hours(totals.time_saved_measured_minutes)} medidas · resto estimado`,
          },
          { value: `€${Math.round(totals.revenue).toLocaleString("pt-PT")}`, label: "negócio" },
          { value: String(totals.reusable), label: "componentes reutilizáveis" },
        ].map((kpi) => (
          <div key={kpi.label} className="bg-card px-4 py-3">
            <div className="font-mono text-xl font-semibold leading-none">{kpi.value}</div>
            <div className="mt-1 text-[11px] text-muted-foreground">{kpi.label}</div>
            {kpi.sub && (
              <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                {kpi.sub}
              </div>
            )}
          </div>
        ))}
      </div>

      {Object.keys(totals.weight_by_type).length > 0 && (
        <>
          <SectionHead
            title="Peso por tipo de resultado"
            eyebrow={range.label}
            note="O peso é do resultado, não da pessoa."
          />
          <div className="grid gap-px overflow-hidden rounded-md border bg-border sm:grid-cols-3 lg:grid-cols-5">
            {Object.entries(totals.weight_by_type)
              .sort((a, b) => b[1] - a[1])
              .map(([type, weight]) => (
                <div key={type} className="bg-card px-4 py-3">
                  <div className="font-mono text-lg font-semibold text-primary">{weight}</div>
                  <div className="text-xs text-muted-foreground">{type.replace(/_/g, " ")}</div>
                </div>
              ))}
            <div className="bg-primary/10 px-4 py-3">
              <div className="font-mono text-lg font-semibold text-primary">{totals.weight_total}</div>
              <div className="text-xs text-muted-foreground">peso total</div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                da equipa, não de ninguém
              </div>
            </div>
          </div>
        </>
      )}

      {suggestions.length > 0 && (
        <>
          <SectionHead
            title="Por decidir"
            eyebrow={`${suggestions.length} sugestões`}
            note="O host diz o que aconteceu. A decisão é humana."
          />
          <div className="flex flex-col gap-2">
            {suggestions.slice(0, 5).map((suggestion) => (
              <div
                key={suggestion.key}
                className="flex flex-wrap items-start gap-4 rounded-md border border-dashed border-primary/50 bg-primary/5 px-4 py-3"
              >
                <div className="flex-1 basis-72">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-primary">
                    {ORIGIN_LABEL[suggestion.kind === "deal_won" ? "crm" : "ai_draft"]} ·{" "}
                    {suggestion.occurred_at.slice(0, 10)}
                  </span>
                  <h3 className="mt-1 text-sm font-semibold">{suggestion.title}</h3>
                  {suggestion.evidence.length > 0 && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      A partir de {suggestion.evidence.slice(0, 3).join(", ")}
                      {suggestion.evidence.length > 3 && ` e mais ${suggestion.evidence.length - 3}`}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => decide(suggestion, "registered")}>
                    Registar
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => decide(suggestion, "ignored")}>
                    Ignorar
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <SectionHead
        title="O que ficou melhor"
        eyebrow={`${range.label} · ${range.sub}`}
        note="Não me digas o que fizeste. Diz-me o que mudou."
      />
      {data.achievements.length === 0 ? (
        <p className="rounded-md border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
          Nada registado neste período. Não é falha — há resultados que demoram mais do que uma semana.
        </p>
      ) : (
        <div className="ml-1 border-l-2 pl-0">
          {data.achievements.map((achievement) => (
            <article key={achievement.id} className="relative border-b py-5 pl-6 last:border-0">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span
                  className={cn(
                    "rounded px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-widest",
                    achievement.level === "game_changer"
                      ? "bg-primary text-primary-foreground"
                      : achievement.level === "big_win"
                        ? "border border-emerald-500 text-emerald-600 dark:text-emerald-400"
                        : "bg-muted text-muted-foreground",
                  )}
                >
                  {LEVEL_LABEL[achievement.level]}
                </span>
                <span className="rounded border border-primary px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-primary">
                  {ORIGIN_LABEL[achievement.source_kind] ?? achievement.source_kind}
                </span>
                <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] font-semibold">
                  peso {achievement.weight}
                </span>
                {achievement.result_types.map((type) => (
                  <span
                    key={type}
                    className="rounded border bg-muted/50 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground"
                  >
                    {type.replace(/_/g, " ")}
                  </span>
                ))}
              </div>
              <h3 className="text-base font-semibold">{achievement.title}</h3>
              {achievement.description && (
                <p className="mt-1 max-w-[62ch] text-sm text-muted-foreground">
                  {achievement.description}
                </p>
              )}
              <div className="mt-2 flex flex-wrap gap-3 text-xs text-muted-foreground">
                <span>{achievement.owner_name ?? "—"}</span>
                {achievement.contributors.length > 0 && (
                  <span>com {achievement.contributors.join(", ")}</span>
                )}
                <span className="font-mono">{achievement.occurred_on?.slice(0, 10)}</span>
              </div>
            </article>
          ))}
        </div>
      )}

      {people.size > 0 && (
        <>
          <SectionHead
            title="Quem participou"
            eyebrow={range.label}
            note="Ordem alfabética. Sem peso por pessoa — o peso é do resultado."
          />
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[...people.entries()]
              .sort((a, b) => a[0].localeCompare(b[0], "pt"))
              .map(([name, entry]) => (
                <div key={name} className="rounded-md border bg-card px-4 py-3">
                  <b className="text-sm">{name}</b>
                  <div className="mt-2 grid grid-cols-3 gap-2 border-y py-2">
                    <Stat value={String(entry.wins)} label="wins" />
                    <Stat value={String(entry.clients.size || "—")} label="clientes" />
                    <Stat
                      value={
                        entry.top === "game_changer" ? "GC" : entry.top === "big_win" ? "BW" : "Win"
                      }
                      label="maior nível"
                    />
                  </div>
                </div>
              ))}
          </div>
        </>
      )}
    </div>
  )
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <span className="flex flex-col">
      <b className="font-mono text-base leading-none">{value}</b>
      <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
    </span>
  )
}

function SectionHead({ title, eyebrow, note }: { title: string; eyebrow: string; note: string }) {
  return (
    <div className="mb-3 mt-9 flex flex-wrap items-baseline gap-3 border-b pb-2">
      <h2 className="text-base font-semibold">{title}</h2>
      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        {eyebrow}
      </span>
      <span className="ml-auto text-xs text-muted-foreground">{note}</span>
    </div>
  )
}
